import React from "react";
import { Icon, notify } from "../registry";
import type { SiteBundle } from "./types";
import { COLOR_PRESETS, FONTS, STYLE_CHOICES, printKitColors, stylesFromCatalog, suggestSlug, siteSlugOk, slugify, fontCss, stable } from "./util";
import { Field, TextField, TextArea, Toggle, ChipsInput, SelectField } from "./fields";

/* ============================================================
   Website builder — first run. Four short steps, pre-filled from the
   workspace (Company settings, service areas, Print Kit colors, price book,
   warranty), then POST /sites/me. The generator falls back to workspace data
   for anything left blank, so every question can be skipped.
   ============================================================ */

const STEPS = ["Your business", "What you install", "How you build", "Your look"];
const BUILD_STAGES = ["Reading your business details", "Writing your home page", "Writing a page for each fence style", "Writing your town pages", "About, FAQs and care guide", "Placing your job photos", "Titles and descriptions for Google"];

export function prefillAnswers(store: any, auth: any) {
  const co = (store && store.company) || {};
  const s = (store && store.settings) || {};
  const name = co.name || s.companyName || "";
  const city = co.city || s.companyCity || "";
  const state = co.state || s.companyState || "";
  const areas: string[] = (Array.isArray(s.serviceAreas) ? s.serviceAreas : []).map((a: any) => (typeof a === "string" ? a : a && (a.name || a.city))).filter(Boolean);
  const products = (store && store.customProducts) || [];   // the built-in catalog has every category; only their own products say what they sell
  const fromBook = stylesFromCatalog(products);
  const pk = printKitColors(s);
  const warranty = Number(s.warrantyYears) > 0 ? `${s.warrantyYears}-year written workmanship warranty` : "";
  return {
    basics: { name, phone: co.phone || s.companyPhone || "", email: co.email || (auth && auth.user && auth.user.email) || "", city, state },
    slug: suggestSlug(name),
    styles: fromBook.length ? fromBook : STYLE_CHOICES.map((x) => x.slug),
    cities: areas.length ? areas.slice(0, 12) : city ? [city] : [],
    postMethod: "", warrantyText: warranty, permitsHandled: true, license: "", insured: true,
    ownerName: (auth && auth.user && auth.user.name) || "", ownerStory: "", differentiators: ["", "", ""], yearFounded: "",
    colors: pk || { ...COLOR_PRESETS[0].colors }, font: "figtree",
  };
}

export function Wizard({ store, auth, live, onCreated, onCancel }: { store: any; auth: any; live: boolean; onCreated: (b: SiteBundle, basics: any, answers: any) => void; onCancel?: () => void }) {
  const DB: any = (window as any).VFP_DB;
  const [a, setA] = React.useState<any>(() => prefillAnswers(store, auth));
  const [step, setStep] = React.useState(0);
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [building, setBuilding] = React.useState(false);
  const [stage, setStage] = React.useState(0);
  const [err, setErr] = React.useState("");
  const set = (p: any) => setA((x: any) => ({ ...x, ...p }));
  const setB = (p: any) => setA((x: any) => {
    const basics = { ...x.basics, ...p };
    return { ...x, basics, slug: !slugTouched && p.name != null ? suggestSlug(p.name) : x.slug };
  });
  const s = (store && store.settings) || {};
  const co = (store && store.company) || {};
  const pk = printKitColors(s);
  const presets = pk ? [{ id: "printkit", label: "My Print Kit", colors: pk }, ...COLOR_PRESETS] : COLOR_PRESETS;
  const root = "fenceworks.site";

  React.useEffect(() => {
    if (!building) return;
    const t = setInterval(() => setStage((n) => Math.min(n + 1, BUILD_STAGES.length - 1)), 6500);
    return () => clearInterval(t);
  }, [building]);

  const answers = () => ({
    slug: a.slug, styles: a.styles, cities: a.cities, hubCity: a.basics.city || undefined, region: a.basics.state || undefined,
    postMethod: a.postMethod.trim() || undefined, warrantyText: a.warrantyText.trim() || undefined, permitsHandled: !!a.permitsHandled,
    license: a.license.trim() || undefined, insured: !!a.insured, ownerName: a.ownerName.trim() || undefined, ownerStory: a.ownerStory.trim() || undefined,
    differentiators: a.differentiators.map((x: string) => x.trim()).filter(Boolean), colors: a.colors, font: a.font,
    yearFounded: Number(a.yearFounded) > 1900 ? Number(a.yearFounded) : undefined,
  });
  const build = async () => {
    if (!siteSlugOk(a.slug)) { setStep(0); notify("Pick a web address: 3–40 letters, numbers or dashes.", { kind: "error" }); return; }
    if (!a.styles.length) { setStep(1); notify("Pick at least one fence style you install.", { kind: "error" }); return; }
    setErr(""); setStage(0); setBuilding(true);
    const ans = answers();
    if (!live) {
      // sandbox: pretend for a beat, then hand the answers to the sample
      setTimeout(() => { setBuilding(false); onCreated(null as any, a.basics, ans); }, 2600);
      return;
    }
    try {
      const r = await DB.siteCreate(ans);
      onCreated({ ...r, domains: r.domains || [] }, a.basics, ans);
    } catch (e: any) {
      setBuilding(false);
      if (e.status === 409) { notify("You already have a site — opening it."); onCreated(null as any, null, null); return; }
      setErr(e.message || "Couldn't build the site. Nothing was charged — try again.");
    }
  };

  if (building) {
    return (
      <div className="card pad ws-building">
        <div className="ws-spin big" />
        <h2 className="display" style={{ fontSize: 22, margin: "14px 0 4px" }}>Writing your pages…</h2>
        <p className="muted" style={{ margin: "0 0 18px" }}>This takes a minute or two. Stay on this screen — your site opens here when it's ready.</p>
        <div className="ws-stages">
          {BUILD_STAGES.map((t, i) => (
            <div key={t} className={"ws-stage" + (i < stage ? " done" : i === stage ? " now" : "")}>
              <span className="ws-stage-dot">{i < stage ? <Icon name="check" size={11} /> : null}</span>{t}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const toggleStyle = (slug: string) => set({ styles: a.styles.includes(slug) ? a.styles.filter((x: string) => x !== slug) : [...a.styles, slug] });
  const pulled = [
    ["Logo", !!co.logo], ["Google review link", !!s.googleReviewUrl], ["Social links", !!(s.socialLinks && Object.values(s.socialLinks).some(Boolean))],
    ["Chat & instant quote", !!(s.widget && s.widget.token)], ["Service areas", Array.isArray(s.serviceAreas) && s.serviceAreas.length > 0], ["Print Kit colors", !!pk],
  ] as [string, boolean][];

  return (
    <div className="ws-wizard">
      <div className="card pad ws-wiz-hero">
        <div style={{ maxWidth: 620 }}>
          <span className="kicker kicker-teal">New · Growth plan</span>
          <h2 className="display" style={{ fontSize: 24, margin: "6px 0 8px" }}>Your website, built from your account in about two minutes.</h2>
          <p className="muted" style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>A full contractor site — home, a page per fence style, town pages, about, financing, blog — in the proven Kodiak design. Every quote request lands in your Customers. You can change every word before it goes live.</p>
        </div>
      </div>

      <div className="ws-steps" role="tablist" aria-label="Setup steps">
        {STEPS.map((t, i) => (
          <button key={t} type="button" className={"ws-stepbtn" + (i === step ? " on" : i < step ? " done" : "")} onClick={() => setStep(i)} aria-current={i === step ? "step" : undefined}>
            <span className="ws-stepn">{i < step ? <Icon name="check" size={12} /> : i + 1}</span><span className="ws-stept">{t}</span>
          </button>
        ))}
      </div>

      <div className="card pad ws-wiz-body">
        {step === 0 && (
          <>
            <h3 className="display ws-wiz-h">Your business</h3>
            <p className="muted ws-wiz-sub">From your Company settings. Changes here only affect the website.</p>
            <div className="ws-2"><TextField label="Business name" value={a.basics.name} onChange={(v: string) => setB({ name: v })} /><TextField label="Phone" type="tel" value={a.basics.phone} onChange={(v: string) => setB({ phone: v })} /></div>
            <div className="ws-3"><TextField label="Email" type="email" value={a.basics.email} onChange={(v: string) => setB({ email: v })} /><TextField label="City" value={a.basics.city} onChange={(v: string) => setB({ city: v })} /><TextField label="State" value={a.basics.state} onChange={(v: string) => setB({ state: v })} /></div>
            <Field label="Your free web address" hint={siteSlugOk(a.slug) ? "You can connect your own domain (acmefence.com) right after." : <span style={{ color: "var(--amber)" }}>3–40 letters, numbers or dashes.</span>}>
              <div className="ws-slug"><span>https://</span><input value={a.slug} aria-label="Web address" onChange={(e: any) => { setSlugTouched(true); set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 40) }); }} onBlur={() => set({ slug: slugify(a.slug, 40) })} /><span>.{root}</span></div>
            </Field>
            <div className="ws-pulled">
              <span className="faint" style={{ fontSize: 12 }}>Also pulled from your account:</span>
              {pulled.map(([t, ok]) => <span key={t} className={"ws-pill" + (ok ? " ok" : "")}>{ok ? <Icon name="check" size={11} /> : <span className="ws-pill-dot" />}{t}</span>)}
            </div>
          </>
        )}
        {step === 1 && (
          <>
            <h3 className="display ws-wiz-h">What you install</h3>
            <p className="muted ws-wiz-sub">Each style gets its own page. Pre-ticked from your price book.</p>
            <div className="ws-stylepick">
              {STYLE_CHOICES.map((x) => {
                const on = a.styles.includes(x.slug);
                return (
                  <button key={x.slug} type="button" className={"ws-stylecard" + (on ? " on" : "")} aria-pressed={on} onClick={() => toggleStyle(x.slug)}>
                    <span className="ws-check-box">{on && <Icon name="check" size={12} />}</span>
                    <span><b>{x.name}</b><span className="faint">{x.hint}</span></span>
                  </button>
                );
              })}
            </div>
            <ChipsInput label="Towns you want to win" placeholder="Type a town, press Enter" value={a.cities} onChange={(v: string[]) => set({ cities: v })}
              hint="Each town gets its own page. Start with the 5–10 you work in most — you can add more later." />
          </>
        )}
        {step === 2 && (
          <>
            <h3 className="display ws-wiz-h">How you build</h3>
            <p className="muted ws-wiz-sub">Your site only claims what you tell it. Skip anything that doesn't apply.</p>
            <TextArea label="How you set posts" rows={2} value={a.postMethod} onChange={(v: string) => set({ postMethod: v })} placeholder="2-3/8″ galvanized steel posts set in concrete below the frost line" />
            <div className="ws-2"><TextField label="Warranty" value={a.warrantyText} onChange={(v: string) => set({ warrantyText: v })} placeholder="5-year written workmanship warranty" /><TextField label="License # (optional)" value={a.license} onChange={(v: string) => set({ license: v })} /></div>
            <div className="ws-2">
              <Toggle label="We handle permits & 811 locates" value={a.permitsHandled} onChange={(v: boolean) => set({ permitsHandled: v })} />
              <Toggle label="We're insured" value={a.insured} onChange={(v: boolean) => set({ insured: v })} />
            </div>
            <Field label="Three things that set you apart">
              <div className="ws-list">{a.differentiators.map((x: string, i: number) => (
                <input key={i} className="input ws-input" value={x} placeholder={["Family-owned since 2012", "Same-week written quotes", "We clean up every scrap"][i]} onChange={(e: any) => set({ differentiators: a.differentiators.map((y: string, j: number) => (j === i ? e.target.value : y)) })} />
              ))}</div>
            </Field>
            <div className="ws-2"><TextField label="Owner's name (optional)" value={a.ownerName} onChange={(v: string) => set({ ownerName: v })} /><TextField label="Year founded (optional)" inputMode="numeric" value={a.yearFounded} onChange={(v: string) => set({ yearFounded: v.replace(/\D/g, "").slice(0, 4) })} /></div>
            <TextArea label="Your story in a few sentences (optional)" rows={3} value={a.ownerStory} onChange={(v: string) => set({ ownerStory: v })} placeholder="How you got started, and what you won't cut corners on." />
          </>
        )}
        {step === 3 && (
          <>
            <h3 className="display ws-wiz-h">Your look</h3>
            <p className="muted ws-wiz-sub">Pick colors close to your logo and trucks. You can fine-tune every color later.</p>
            <div className="ws-presets big">
              {presets.map((p) => {
                const on = stable(p.colors) === stable(a.colors);
                return (
                  <button key={p.id} type="button" className={"ws-preset" + (on ? " on" : "")} onClick={() => set({ colors: { ...p.colors } })}>
                    <span className="ws-mini" style={{ background: p.colors.cream, fontFamily: fontCss(a.font) }}>
                      <span style={{ background: p.colors.ink }}><i style={{ background: p.colors.accent }} /></span>
                      <b style={{ color: p.colors.heading }}>Aa</b><em style={{ background: p.colors.accent }} />
                    </span>
                    {p.label}
                  </button>
                );
              })}
            </div>
            <SelectField label="Font" value={a.font} onChange={(v: string) => set({ font: v })} options={FONTS.map((f) => [f.id, f.label])} />
            {co.logo ? <p className="faint" style={{ fontSize: 12.5 }}><Icon name="check" size={12} style={{ verticalAlign: -2 }} /> Your logo from Settings goes in the header.</p>
              : <p className="faint" style={{ fontSize: 12.5 }}>No logo yet — your name is set as a clean wordmark. Add a logo any time under Design.</p>}
          </>
        )}
        {err && <div className="ws-err" style={{ marginTop: 12 }}>{err}</div>}
        <div className="ws-wiz-foot">
          {step > 0 ? <button type="button" className="btn btn-ghost" onClick={() => setStep(step - 1)}>Back</button> : onCancel ? <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button> : <span />}
          {step < STEPS.length - 1
            ? <button type="button" className="btn btn-primary" onClick={() => setStep(step + 1)}>Next<Icon name="chevright" size={15} /></button>
            : <button type="button" className="btn btn-primary" onClick={build}><Icon name="bolt" size={15} />Build my site</button>}
        </div>
        {step === STEPS.length - 1 && <p className="faint" style={{ fontSize: 12, textAlign: "right", margin: "8px 0 0" }}>Builds a draft only — nothing goes live until you press Publish.</p>}
      </div>
    </div>
  );
}
